/* eslint-disable @typescript-eslint/no-explicit-any */
import { exists } from "shared/lib/exists"
import { hasSchemaType } from "shared/lib/json-schema/hasSchemaType"
import { RJSFFormData, RJSFSchema } from "shared/types/RJSF"

import I18n from "i18next"
import {
  BooleanSchema,
  NumberSchema,
  Schema,
  StringSchema,
  ValidationError,
  boolean,
  number,
  object,
  string
} from "yup"

function buildGenericSchema<
  V extends
    | StringSchema<string | null | undefined>
    | NumberSchema<number | null | undefined>
    | BooleanSchema<boolean | null | undefined>
>(schema: RJSFSchema, validator: V, options?: { required?: boolean }) {
  let result: V = validator

  if (schema.title) {
    result = result.label(schema.title) as V
  }

  const requiredError = I18n.t("live:Validation.required", {
    fieldName: schema.title
  })

  if (hasSchemaType(schema, "null")) {
    result = result.nullable() as V
  } else {
    result = result.nonNullable(requiredError) as V
  }
  if (!options?.required) {
    result = result.optional() as V
  } else {
    result = result.defined(requiredError) as V
  }
  if (!!options?.required && !hasSchemaType(schema, "null")) {
    result = result.required(requiredError) as V
  }

  return result
}

function buildStringSchema(
  schema: RJSFSchema,
  options?: { required?: boolean }
) {
  let validator: StringSchema<string | null | undefined> = string()

  validator = buildGenericSchema(schema, validator, options)

  if (schema.enum) {
    validator = validator.oneOf(
      schema.enum as string[],
      I18n.t("live:Validation.oneOf", {
        fieldName: schema.title,
        fieldValues: schema.enum.join(", ")
      })
    )
  }

  if (exists(schema.minLength)) {
    validator = validator.min(
      schema.minLength,
      I18n.t("live:Validation.minLength", {
        fieldName: schema.title,
        minLength: schema.minLength
      })
    )
  }

  if (exists(schema.maxLength)) {
    validator = validator.max(
      schema.maxLength,
      I18n.t("live:Validation.maxLength", {
        fieldName: schema.title,
        maxLength: schema.maxLength
      })
    )
  }

  return validator
}

function buildNumberSchema(
  schema: RJSFSchema,
  options?: { required?: boolean }
) {
  let validator: NumberSchema<number | null | undefined> = number()

  validator = buildGenericSchema(schema, validator, options)

  if (schema.enum) {
    validator = validator.oneOf(
      schema.enum as number[],
      I18n.t("live:Validation.oneOf", {
        fieldName: schema.title,
        fieldValues: schema.enum.join(", ")
      })
    )
  }

  if (exists(schema.minimum)) {
    validator = validator.min(
      schema.minimum,
      I18n.t("live:Validation.min", {
        fieldName: schema.title,
        min: schema.minimum
      })
    )
  }

  if (exists(schema.maximum)) {
    validator = validator.max(
      schema.maximum,
      I18n.t("live:Validation.max", {
        fieldName: schema.title,
        max: schema.maximum
      })
    )
  }

  return validator
}
function buildBooleanSchema(
  schema: RJSFSchema,
  options?: { required?: boolean }
) {
  return buildGenericSchema(schema, boolean(), options)
}

function testAll(
  value: RJSFFormData,
  path: string,
  schemas: Schema<any, any, any, "">[]
) {
  const errors: ValidationError[] = []

  schemas.forEach(subSchema => {
    try {
      subSchema?.validateSync(value, { strict: true })
    } catch (e) {
      const err = e as ValidationError
      err.path = `${path}.${(e as ValidationError).path}`
      errors.push(err)
    }
  })
  return errors
}

function buildObjectSchema(schema: RJSFSchema) {
  if (schema.properties) {
    return object(
      Object.fromEntries(
        Object.entries(schema.properties).map(
          ([key, value]) =>
            [
              key,
              buildSchema(value, {
                required: schema.required?.includes(key)
              })
            ] as [string, Schema]
        )
      )
    )
  }

  if (schema.allOf) {
    const schemas = schema.allOf.map(subSchema => buildSchema(subSchema))

    return object().test({
      test(value, context) {
        const errors = testAll(value, context.path, schemas)

        if (errors.length > 0) {
          const error = context.createError({
            type: "objectAllOf",
            message: I18n.t("live:Validation.objectAllOf")
          })

          return new ValidationError(
            [error, ...errors],
            value,
            context.path,
            "objectAllOf"
          )
        }
        return true
      }
    })
  }

  if (schema.anyOf) {
    const schemas = schema.anyOf.map(subSchema => buildSchema(subSchema))

    return object().test({
      test(value, context) {
        const errors = testAll(value, context.path, schemas)

        if (errors.length === schemas.length) {
          const error = context.createError({
            type: "objectAnyOf",
            message: I18n.t("live:Validation.objectAnyOf")
          })

          return new ValidationError(
            [error, ...errors],
            value,
            context.path,
            "objectAnyOf"
          )
        }
        return true
      }
    })
  }

  if (schema.oneOf) {
    const schemas = schema.oneOf.map(subSchema => buildSchema(subSchema))

    return object().test({
      test(value, context) {
        const errors = testAll(value, context.path, schemas)

        if (errors.length !== schemas.length - 1) {
          const error = context.createError({
            type: "objectOneOf",
            message: I18n.t("live:Validation.objectOneOf")
          })

          return new ValidationError(
            [error, ...errors],
            value,
            context.path,
            "objectOneOf"
          )
        }
        return true
      }
    })
  }

  return object({})
}

export function buildSchema(
  schema: RJSFSchema,
  options?: { required?: boolean }
): Schema {
  if (hasSchemaType(schema, "string")) {
    return buildStringSchema(schema, options)
  }
  if (hasSchemaType(schema, ["number", "integer"])) {
    return buildNumberSchema(schema, options)
  }
  if (hasSchemaType(schema, "boolean")) {
    return buildBooleanSchema(schema, options)
  }

  return buildObjectSchema(schema)
}
